import { useState, useCallback, useRef } from "react";
import { fetchJobSearch, type SearchResult } from "@noiman/shared-graphql";

export { type SearchResult };

export type SearchEntryType = "chapter" | "glossary" | "lesson" | "textbook" | "video" | "cs";

export type SearchIndexEntry = {
  type: SearchEntryType;
  id: string;
  title: string;
  excerpt: string;
  searchText?: string;
  url: string;
  category?: string;
  isTrialVisible?: boolean;
  /** 結果全体での順位 (0 が先頭)。1 列に並べる窓 (LgSearchWindow) がこの順で並べる */
  rank?: number;
  /** 結果の左に置く絵の住所。無ければ手描きの印を置く */
  art?: string;
};

export type SearchGroup = {
  type: SearchEntryType;
  items: SearchIndexEntry[];
};

export interface UseSearchOptions {
  limit?: number;
}

export function useSearch() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  // 古い問い合わせの返事が、新しい問い合わせの結果を上書きしないための番号
  const latest = useRef(0);

  const search = useCallback(async (query: string, options?: UseSearchOptions) => {
    const id = ++latest.current;
    if (!query.trim()) {
      setGroups([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.set("query", query);
      if (options?.limit) fd.set("limit", String(options.limit));
      const res = await fetch("/api/luagate-search", { method: "POST", body: fd });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "search failed");
      if (id !== latest.current) return;
      setGroups(data.groups || []);
    } catch (err) {
      if (id !== latest.current) return;
      const e = err instanceof Error ? err : new Error("Search failed");
      setError(e);
      console.error("Search error:", e);
      setGroups([]);
    } finally {
      if (id === latest.current) setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    latest.current += 1;
    setGroups([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return { search, clear, groups, isLoading, error };
}

/**
 * 求人専用ベクトル検索フック (jobs ページ等で利用)
 * GraphQL fetchJobSearch を直接呼ぶ既存仕様を維持。
 */
export function useJobSearch() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);

  const searchJobs = useCallback(async (query: string, limit = 20) => {
    if (!query.trim()) {
      setResults([]);
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchResults = await fetchJobSearch({
        query,
        limit,
      });

      setResults(searchResults);
      return searchResults;
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Job search failed");
      setError(e);
      console.error("Job search error:", e);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    searchJobs,
    clear,
    results,
    isLoading,
    error,
  };
}
